import PropTypes from "prop-types";
import VuiBox from "components/ui/VuiBox";
import VuiTypography from "components/ui/VuiTypography";
import { CHART_COLORS, ACCENT_COLORS } from "utils/designTokens";

/**
 * TopicTagsStrip
 *
 * A horizontally scrollable row of pill buttons, each showing a topic label
 * and its article/post count. Clicking a pill calls onSelect(topic).
 * Clicking the active pill again clears the filter (calls onSelect(null)).
 *
 * Data shape expected: [{ text: "Flow Diverters", frequency: 8 }, ...]
 *
 * Used by:
 *   - Press Digest v2  (topic filter for article feed)
 *   - KOL Insights  (topic/product filter)  [M004]
 *
 * Props:
 *   topics     — array of { text, frequency }
 *   selected   — currently selected topic string, or null
 *   onSelect   — (topic: string | null) => void
 *   loading    — show skeleton pills while loading
 */
function TopicTagsStrip({ topics, selected, onSelect, loading }) {

  const handlePillClick = (topic) => {
    onSelect(selected === topic ? null : topic);
  };

  if (loading) {
    return (
      <VuiBox
        sx={{
          display: "flex",
          flexWrap: "wrap",
          gap: 1,
          pb: 0.5,
        }}
      >
        {[80, 120, 95, 110, 75, 100].map((w, i) => (
          <VuiBox
            key={i}
            sx={{
              width: w,
              height: 28,
              borderRadius: "14px",
              background: "rgba(255,255,255,0.06)",
              flexShrink: 0,
            }}
          />
        ))}
      </VuiBox>
    );
  }

  if (!topics || topics.length === 0) return null;

  return (
    <VuiBox
      sx={{
        display: "flex",
        flexWrap: "wrap",
        gap: 1,
        pb: 0.5,
      }}
    >
      {topics.map((topic, index) => {
        const isActive = selected === topic.text;
        // Cycle through chart colours for variety; active pill uses primary accent
        const pillColor = CHART_COLORS[index % CHART_COLORS.length];

        return (
          <VuiBox
            key={topic.text}
            onClick={() => handlePillClick(topic.text)}
            sx={{
              display: "flex",
              alignItems: "center",
              gap: 0.75,
              px: 1.5,
              py: 0.5,
              borderRadius: "14px",
              border: `1px solid ${isActive ? ACCENT_COLORS.primary : "rgba(255,255,255,0.12)"}`,
              background: isActive
                ? `rgba(37, 99, 235, 0.18)`
                : "rgba(255,255,255,0.05)",
              cursor: "pointer",
              flexShrink: 0,
              transition: "all 0.18s ease",
              "&:hover": {
                background: isActive
                  ? "rgba(37, 99, 235, 0.25)"
                  : "rgba(255,255,255,0.09)",
                borderColor: isActive ? ACCENT_COLORS.primary : "rgba(255,255,255,0.25)",
              },
              userSelect: "none",
            }}
          >
            {/* Colour dot */}
            <VuiBox
              sx={{
                width: 7,
                height: 7,
                borderRadius: "50%",
                background: isActive ? ACCENT_COLORS.primary : pillColor,
                flexShrink: 0,
                opacity: isActive ? 1 : 0.7,
              }}
            />
            <VuiTypography
              variant="caption"
              color="white"
              fontWeight={isActive ? "bold" : "regular"}
              sx={{
                fontSize: "0.75rem",
                whiteSpace: "nowrap",
                opacity: isActive ? 1 : 0.8,
              }}
            >
              {topic.text}
            </VuiTypography>
            {/* Count badge */}
            <VuiBox
              sx={{
                px: 0.75,
                py: 0.1,
                borderRadius: "8px",
                background: isActive
                  ? "rgba(37, 99, 235, 0.35)"
                  : "rgba(255,255,255,0.1)",
              }}
            >
              <VuiTypography
                variant="caption"
                sx={{
                  fontSize: "0.68rem",
                  color: isActive ? "#93c5fd" : "rgba(255,255,255,0.5)",
                  fontWeight: "600",
                }}
              >
                {topic.frequency}
              </VuiTypography>
            </VuiBox>
          </VuiBox>
        );
      })}
    </VuiBox>
  );
}

TopicTagsStrip.propTypes = {
  topics: PropTypes.arrayOf(
    PropTypes.shape({
      text: PropTypes.string.isRequired,
      frequency: PropTypes.number.isRequired,
    })
  ),
  selected: PropTypes.string,
  onSelect: PropTypes.func.isRequired,
  loading: PropTypes.bool,
};

TopicTagsStrip.defaultProps = {
  topics: [],
  selected: null,
  loading: false,
};

export default TopicTagsStrip;
